import {
  Alert,
  AlertProps,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Snackbar,
} from '@mui/material'
import { DataGrid, GridRowModel } from '@mui/x-data-grid'
import { doc, setDoc, updateDoc } from 'firebase/firestore'
import { useCallback, useRef, useState } from 'react'
import {
  UserRole,
  useUserTableColumns,
  useUserTableRows,
} from '../../../providers/tables'
import { PublicUserExtended } from '../../../providers/users'
import { firestore } from '../../firebaseInit'
import { useAuth } from '../../providers/auth'
import TableToolbar from './table-toolbar'

function computeMutation(newRow: GridRowModel, oldRow: GridRowModel) {
  if (JSON.stringify(newRow) === JSON.stringify(oldRow)) return null
  return 'Spara ändring för ' + newRow.email
}

const updateUser = (user: PublicUserExtended) => {
  const userRef = doc(firestore, 'users', user.uid)
  return setDoc(userRef, user, { merge: true })
}

export default function UserTable() {
  const { user } = useAuth()
  const { rows, loading, error } = useUserTableRows()
  const noButtonRef = useRef<HTMLButtonElement>(null)
  const [promiseArguments, setPromiseArguments] = useState<any>(null)
  const [roleLoading, setRoleLoading] = useState<string | null>(null)

  const [snackbar, setSnackbar] = useState<Pick<
    AlertProps,
    'children' | 'severity'
  > | null>(null)

  const handleCloseSnackbar = () => setSnackbar(null)

  const processRowUpdate = useCallback(
    (newRow: GridRowModel, oldRow: GridRowModel) =>
      new Promise<GridRowModel>((resolve, reject) => {
        const mutation = computeMutation(newRow, oldRow)
        if (mutation) {
          setPromiseArguments({ resolve, reject, newRow, oldRow })
        } else {
          resolve(oldRow)
        }
      }),
    []
  )

  const handleNo = () => {
    const { oldRow, resolve } = promiseArguments
    resolve(oldRow)
    setPromiseArguments(null)
  }

  const handleYes = async () => {
    const { newRow, oldRow, reject, resolve } = promiseArguments
    try {
      await updateUser(newRow)
      setSnackbar({ children: 'Användare uppdaterad', severity: 'success' })
      setPromiseArguments(null)
      resolve(newRow)
    } catch (error) {
      console.log(error)
      setSnackbar({
        children: 'Användaren kunde inte uppdateras',
        severity: 'error',
      })
      reject(oldRow)
      setPromiseArguments(null)
    }
  }

  const handleRoleChange = useCallback(
    async (uid: string, role: UserRole, enabled: boolean) => {
      const roleLabel = role === 'admin' ? 'admin' : 'tränare'
      const field = role === 'admin' ? 'isAdmin' : 'isCoach'

      setRoleLoading(`${uid}:${role}`)

      try {
        await updateDoc(doc(firestore, 'users', uid), { [field]: enabled })
        setSnackbar({
          children: enabled
            ? `Rollen ${roleLabel} lades till`
            : `Rollen ${roleLabel} togs bort`,
          severity: 'success',
        })
      } catch (error) {
        console.log(error)
        setSnackbar({
          children: 'Rollen kunde inte uppdateras',
          severity: 'error',
        })
      } finally {
        setRoleLoading(null)
      }
    },
    []
  )

  const columns = useUserTableColumns({
    currentUserId: user?.uid,
    roleLoading,
    onRoleChange: handleRoleChange,
  })

  const handleEntered = () => {
    noButtonRef.current?.focus()
  }

  const renderConfirmDialog = () => {
    if (!promiseArguments) {
      return null
    }

    const { newRow, oldRow } = promiseArguments
    const mutation = computeMutation(newRow, oldRow)

    return (
      <Dialog
        maxWidth="xs"
        TransitionProps={{ onEntered: handleEntered }}
        open={!!promiseArguments}>
        <DialogTitle>Are you sure?</DialogTitle>
        <DialogContent dividers>{mutation}</DialogContent>
        <DialogActions>
          <Button ref={noButtonRef} onClick={handleNo}>
            No
          </Button>
          <Button onClick={handleYes}>Yes</Button>
        </DialogActions>
      </Dialog>
    )
  }

  return (
    <>
      {renderConfirmDialog()}
      {!error && (
        <DataGrid
          autoHeight
          experimentalFeatures={{ newEditingApi: true }}
          hideFooterSelectedRowCount
          components={{ Toolbar: TableToolbar }}
          processRowUpdate={processRowUpdate}
          loading={loading}
          editMode="row"
          pageSize={10}
          rowsPerPageOptions={[10]}
          columns={columns}
          getRowId={(row) => row.uid}
          rows={rows}
          disableSelectionOnClick
        />
      )}
      {error && <DataGrid error={error} columns={[]} rows={[]} />}
      {!!snackbar && (
        <Snackbar
          open
          onClick={handleCloseSnackbar}
          onClose={handleCloseSnackbar}
          autoHideDuration={6000}>
          <Alert {...snackbar} onClose={handleCloseSnackbar} />
        </Snackbar>
      )}
    </>
  )
}
